<template>
  <div class="sms-monitor">

    <!-- ── Header card ─────────────────────────────────────────────── -->
    <v-card class="monitor-card mb-4" elevation="0">
      <div class="monitor-header px-6 pt-5 pb-4">
        <div class="d-flex align-center">
          <div>
            <div class="monitor-title">SMS Auto-Validations</div>
            <div class="monitor-subtitle">
              Real-time tracking of MoMo payment auto-confirmations
            </div>
          </div>
          <v-spacer />
          <v-btn
            outlined small color="primary"
            :loading="loading" class="refresh-btn"
            @click="fetchLogs"
          >
            <v-icon small left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </div>

        <!-- Summary pills -->
        <div class="summary-row mt-4">
          <button
            v-for="(count, key) in summary"
            :key="key"
            class="summary-pill"
            :class="[`pill--${key}`, selectedStatus === key ? 'pill--active' : '']"
            @click="filterByStatus(key)"
          >
            <span class="pill-dot" />
            <span class="pill-label">{{ statusLabel(key) }}</span>
            <span class="pill-count">{{ count }}</span>
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter-bar px-6 pb-4">
        <v-row dense>
          <v-col cols="12" sm="3">
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              label="Status"
              outlined dense clearable hide-details
              class="filter-input"
              @change="fetchLogs"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="selectedFeature"
              :items="featureOptions"
              label="Payment type"
              outlined dense clearable hide-details
              class="filter-input"
              @change="fetchLogs"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="query"
              label="Search user, phone, error…"
              outlined dense hide-details
              prepend-inner-icon="mdi-magnify"
              class="filter-input"
              @keyup.enter="fetchLogs"
            />
          </v-col>
          <v-col cols="12" sm="1" class="d-flex align-center">
            <v-btn icon color="primary" :disabled="loading" @click="fetchLogs">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-card>

    <!-- ── Table card ───────────────────────────────────────────────── -->
    <v-card class="monitor-card" elevation="0">
      <v-data-table
        :headers="headers"
        :items="groupedLogs"
        :loading="loading"
        item-key="group_key"
        class="sms-table"
        :items-per-page="20"
        :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
        disable-sort
      >
        <!-- Date -->
        <template #[`item.date`]="{ item }">
          <div class="cell-date">{{ formatDate(item.latest.created_at) }}</div>
          <div v-if="item.history.length > 1" class="cell-attempts">
            <v-icon x-small color="grey">mdi-history</v-icon>
            {{ item.history.length }} attempts
          </div>
        </template>

        <!-- Status -->
        <template #[`item.status`]="{ item }">
          <div class="d-flex align-center gap-1">
            <span class="status-badge" :class="`badge--${item.latest.status}`">
              {{ statusLabel(item.latest.status) }}
            </span>
          </div>
        </template>

        <!-- Feature -->
        <template #[`item.feature`]="{ item }">
          <div class="cell-feature">{{ item.latest.feature_label || item.latest.feature }}</div>
          <div v-if="item.latest.serial_no" class="cell-sub">#{{ item.latest.serial_no }}</div>
        </template>

        <!-- Client -->
        <template #[`item.user`]="{ item }">
          <div class="cell-user">
            <v-icon x-small color="grey lighten-1" class="mr-1">mdi-account</v-icon>
            {{ item.latest.user || '—' }}
          </div>
        </template>

        <!-- Amounts -->
        <template #[`item.amounts`]="{ item }">
          <div class="amount-block">
            <div>
              <span class="amount-label">SMS</span>
              <span class="amount-value">{{ formatAmount(item.latest.sms_amount) }}</span>
            </div>
            <div>
              <span class="amount-label">Req</span>
              <span class="amount-value">{{ formatAmount(item.latest.request_amount) }}</span>
            </div>
            <div v-if="item.latest.is_partial" class="amount-partial">
              Partial · {{ formatAmount(item.latest.remainder_amount) }} left
            </div>
          </div>
        </template>

        <!-- Phone -->
        <template #[`item.phone`]="{ item }">
          <div class="cell-phone">{{ item.latest.sms_phone || '—' }}</div>
          <div v-if="item.latest.sms_operator" class="cell-sub">{{ item.latest.sms_operator }}</div>
        </template>

        <!-- Request -->
        <template #[`item.request`]="{ item }">
          <div class="cell-sub">{{ item.latest.request_model || '—' }}</div>
          <div v-if="item.latest.request_id" class="cell-id">ID {{ item.latest.request_id }}</div>
        </template>

        <!-- Actions -->
        <template #[`item.actions`]="{ item }">
          <div class="action-row">
            <v-btn
              x-small depressed class="action-btn action-btn--view"
              @click="openDetails(item)"
            >
              <v-icon x-small left>mdi-eye-outline</v-icon>
              View
            </v-btn>
            <v-btn
              v-if="canRetry(item.latest)"
              x-small depressed class="action-btn action-btn--retry"
              :loading="retryingId === item.latest.id"
              :disabled="loading"
              @click="retryValidation(item.latest)"
            >
              <v-icon x-small left>mdi-refresh</v-icon>
              Retry
            </v-btn>
            <v-btn
              v-if="canCancel(item.latest)"
              x-small depressed class="action-btn action-btn--cancel"
              :disabled="loading"
              @click="cancelValidation(item.latest)"
            >
              Cancel
            </v-btn>
          </div>
        </template>

        <template #no-data>
          <div class="empty-state">
            <v-icon large color="grey lighten-2">mdi-message-text-clock-outline</v-icon>
            <div class="mt-2 grey--text">No validations found</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- ── Detail dialog ────────────────────────────────────────────── -->
    <v-dialog v-model="detailsDialog" max-width="760" scrollable>
      <v-card v-if="selectedGroup" class="detail-card">

        <!-- Dialog header -->
        <div class="detail-header" :class="`detail-header--${selectedGroup.latest.status}`">
          <div class="detail-header-main">
            <div class="detail-title">
              {{ selectedGroup.latest.feature_label || selectedGroup.latest.feature }}
            </div>
            <span class="status-badge status-badge--lg" :class="`badge--${selectedGroup.latest.status}`">
              {{ statusLabel(selectedGroup.latest.status) }}
            </span>
          </div>
          <v-btn icon dark small class="detail-close" @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="detail-body">

          <!-- Info grid -->
          <div class="info-grid">
            <div class="info-cell">
              <div class="info-label">Client</div>
              <div class="info-value">{{ selectedGroup.latest.user || '—' }}</div>
            </div>
            <div class="info-cell">
              <div class="info-label">SMS amount</div>
              <div class="info-value highlight">{{ formatAmount(selectedGroup.latest.sms_amount) }}</div>
            </div>
            <div class="info-cell">
              <div class="info-label">Request amount</div>
              <div class="info-value highlight">{{ formatAmount(selectedGroup.latest.request_amount) }}</div>
            </div>
            <div class="info-cell">
              <div class="info-label">Phone</div>
              <div class="info-value">{{ selectedGroup.latest.sms_phone || '—' }}</div>
            </div>
            <div class="info-cell">
              <div class="info-label">Operator</div>
              <div class="info-value">{{ selectedGroup.latest.sms_operator || '—' }}</div>
            </div>
            <div class="info-cell">
              <div class="info-label">Transaction</div>
              <div class="info-value">
                {{ selectedGroup.latest.request_model }}
                <span class="id-chip">#{{ selectedGroup.latest.request_id }}</span>
              </div>
            </div>
          </div>

          <!-- Error -->
          <div v-if="selectedGroup.latest.error_message" class="error-banner mt-3">
            <v-icon small color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
            {{ selectedGroup.latest.error_message }}
          </div>

          <!-- SMS body -->
          <div v-if="detailData && detailData.sms_message && detailData.sms_message.body" class="sms-body mt-4">
            <div class="section-label">SMS body</div>
            <div class="sms-text">{{ detailData.sms_message.body }}</div>
          </div>

          <!-- Attempt history -->
          <div class="mt-5">
            <div class="section-label mb-3">
              Attempt history
              <span class="attempt-count">{{ selectedGroup.history.length }}</span>
            </div>

            <div class="timeline">
              <div
                v-for="(attempt, i) in selectedGroup.history"
                :key="attempt.id"
                class="timeline-item"
                :class="i === 0 ? 'timeline-item--latest' : ''"
              >
                <div class="timeline-dot" :class="`dot--${attempt.status}`" />
                <div class="timeline-line" v-if="i < selectedGroup.history.length - 1" />
                <div class="timeline-content">
                  <div class="d-flex align-center justify-space-between mb-1">
                    <span class="status-badge status-badge--sm" :class="`badge--${attempt.status}`">
                      {{ statusLabel(attempt.status) }}
                    </span>
                    <span v-if="i === 0" class="latest-chip">latest</span>
                  </div>
                  <div class="timeline-meta">
                    {{ formatDate(attempt.created_at) }}
                    <span v-if="attempt.validated_by"> · by {{ attempt.validated_by }}</span>
                    <span class="text-muted"> · Log #{{ attempt.id }}</span>
                  </div>
                  <div v-if="attempt.error_message" class="timeline-error">
                    {{ attempt.error_message }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Retry result -->
          <v-alert
            v-if="retryResult"
            :type="retryResult.success ? 'success' : 'error'"
            dense class="mt-4"
          >
            {{ retryResult.message }}
          </v-alert>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-btn
            v-if="canRetry(selectedGroup.latest)"
            color="warning" small depressed
            :loading="retryingId === selectedGroup.latest.id"
            @click="retryValidation(selectedGroup.latest)"
          >
            <v-icon small left>mdi-refresh</v-icon>
            Retry validation
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="canCancel(selectedGroup.latest)"
            color="error" text small
            :loading="loading"
            @click="cancelValidation(selectedGroup.latest); detailsDialog = false"
          >
            Cancel validation
          </v-btn>
          <v-btn text small @click="detailsDialog = false; retryResult = null">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snack.show" :color="snack.color" top right :timeout="4000">
      {{ snack.message }}
      <template #action>
        <v-btn text @click="snack.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'

const API_BASE = '/api/v1/manager/sms-auto-validations'
const STATUS_ORDER = ['validated', 'partial', 'failed', 'no_match', 'cancelled', 'matched']

export default {
  name: 'SmsAutoValidationsMonitor',

  data() {
    return {
      loading: false,
      logs: [],
      summary: {},
      selectedStatus: '',
      selectedFeature: '',
      query: '',
      detailsDialog: false,
      selectedGroup: null,
      detailData: null,
      retryingId: null,
      retryResult: null,
      snack: { show: false, message: '', color: 'teal darken-2' },

      statusOptions: [
        { text: 'All', value: '' },
        { text: 'Validated', value: 'validated' },
        { text: 'Partial', value: 'partial' },
        { text: 'Failed', value: 'failed' },
        { text: 'No match', value: 'no_match' },
        { text: 'Cancelled', value: 'cancelled' },
      ],

      featureOptions: [
        { text: 'All', value: '' },
        { text: 'Business account', value: 'business_account' },
        { text: 'Campaign deposit', value: 'savings_campaign' },
        { text: 'Withdrawal', value: 'savings_withdrawal' },
        { text: 'Interruption', value: 'savings_interrupt' },
        { text: 'Loan — deposit', value: 'savings_loan_deposit' },
        { text: 'Loan — repayment', value: 'savings_loan_repayment' },
        { text: 'Njangi', value: 'njangi' },
        { text: 'Marketplace', value: 'marketplace' },
        { text: 'Investment', value: 'investment' },
      ],

      headers: [
        { text: 'Date',     value: 'date',    width: '130px' },
        { text: 'Status',   value: 'status',  width: '110px' },
        { text: 'Type',     value: 'feature' },
        { text: 'Client',   value: 'user',    width: '110px' },
        { text: 'Amounts',  value: 'amounts', width: '150px' },
        { text: 'Phone',    value: 'phone',   width: '120px' },
        { text: 'Request',  value: 'request', width: '150px' },
        { text: '',         value: 'actions', sortable: false, width: '190px' },
      ],
    }
  },

  computed: {
    groupedLogs() {
      const groups = {}
      for (const log of this.logs) {
        // Groupement prioritaire : même SMS source ET même transaction cible
        // Fallback : même transaction cible seule (couvre le cas sms_message_id null)
        let key
        if (log.sms_message_id && log.request_id && log.request_model) {
          key = `sms_${log.sms_message_id}__req_${log.request_model}_${log.request_id}`
        } else if (log.request_id && log.request_model) {
          key = `req_${log.request_model}_${log.request_id}`
        } else {
          key = `solo_${log.id}`
        }

        if (!groups[key]) {
          groups[key] = { group_key: key, history: [] }
        }
        groups[key].history.push(log)
      }

      return Object.values(groups).map(g => {
        // Tri : plus récent en premier dans l'historique
        g.history.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        // Meilleur statut = celui le plus à gauche dans STATUS_ORDER
        g.latest = g.history.slice().sort(
          (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
        )[0]
        return g
      }).sort((a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at))
    },
  },

  mounted() { this.fetchLogs() },

  methods: {
    authHeaders() {
      return { headers: { Authorization: `Token ${this.$store.state.token}` } }
    },

    statusLabel(key) {
      return { validated:'Validated', partial:'Partial', failed:'Failed',
               no_match:'No match', cancelled:'Cancelled', matched:'Matched' }[key] || key
    },

    statusColor(status) {
      return { validated:'success', partial:'warning', failed:'error',
               no_match:'orange', cancelled:'grey', matched:'info' }[status] || 'grey'
    },

    formatDate(iso) {
      if (!iso) return '—'
      try { return new Date(iso).toLocaleString('en-GB') } catch { return iso }
    },

    formatAmount(v) {
      if (v === null || v === undefined) return '—'
      return `${Number(v).toLocaleString('fr-FR')} FCFA`
    },

    canCancel(item) {
      return item && ['validated', 'partial'].includes(item.status)
    },

    canRetry(item) {
      return item && ['failed', 'no_match', 'cancelled'].includes(item.status)
    },

    filterByStatus(key) {
      this.selectedStatus = this.selectedStatus === key ? '' : key
      this.fetchLogs()
    },

    showSnack(message, color = 'teal darken-2') {
      this.snack = { show: true, message, color }
    },

    async fetchLogs() {
      this.loading = true
      try {
        const params = {
          limit:   500,
          status:  this.selectedStatus  || undefined,
          feature: this.selectedFeature || undefined,
          q:       this.query           || undefined,
        }
        const { data } = await axios.get(`${API_BASE}/`, { ...this.authHeaders(), params })
        this.logs    = data.results || []
        this.summary = data.summary || {}
      } catch {
        this.showSnack('Failed to load SMS validations', 'red darken-2')
      } finally {
        this.loading = false
      }
    },

    async openDetails(group) {
      this.retryResult = null
      this.detailData  = null
      this.selectedGroup = group
      this.detailsDialog = true
      try {
        const { data } = await axios.get(`${API_BASE}/${group.latest.id}/`, this.authHeaders())
        this.detailData = data
      } catch { /* silently ignore */ }
    },

    async retryValidation(item) {
      if (!this.canRetry(item)) return
      this.retryResult = null
      this.retryingId  = item.id
      try {
        const { data } = await axios.post(`${API_BASE}/${item.id}/retry/`, {}, this.authHeaders())
        const ok = ['validated', 'partial'].includes(data.new_status)
        const labels = { validated:'✅ Successfully validated', partial:'⚠️ Partial validation',
                         failed:'❌ Still failing', no_match:'❌ Still no match' }
        const message = labels[data.new_status] || data.message || `Status: ${data.new_status}`
        this.retryResult = { success: ok, message }
        this.showSnack(message, ok ? 'teal darken-2' : 'red darken-2')
        await this.fetchLogs()
        if (this.detailsDialog && this.selectedGroup) {
          const updated = this.groupedLogs.find(g => g.history.some(h => h.id === item.id))
          if (updated) {
            this.selectedGroup = updated
            try {
              const { data: d } = await axios.get(`${API_BASE}/${updated.latest.id}/`, this.authHeaders())
              this.detailData = d
            } catch { /* ignore */ }
          }
        }
      } catch (e) {
        const msg = e.response?.data?.error || 'Retry failed'
        this.retryResult = { success: false, message: `❌ ${msg}` }
        this.showSnack(msg, 'red darken-2')
      } finally {
        this.retryingId = null
      }
    },

    async cancelValidation(item) {
      if (!this.canCancel(item)) return
      if (!confirm(`Cancel validation #${item.id}?`)) return
      this.loading = true
      try {
        await axios.post(`${API_BASE}/${item.id}/cancel/`, {}, this.authHeaders())
        this.showSnack('Validation cancelled')
        await this.fetchLogs()
      } catch (e) {
        this.showSnack(e.response?.data?.message || 'Cancellation failed', 'red darken-2')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.sms-monitor { padding: 16px; }
.monitor-card { border: 1px solid rgba(0,0,0,.07) !important; border-radius: 12px !important; }
.monitor-title { font-size: 18px; font-weight: 600; color: var(--v-primary-base, #1976d2); }
.monitor-subtitle { font-size: 13px; color: #888; margin-top: 2px; }
.refresh-btn { border-radius: 8px !important; text-transform: none !important; }

/* Summary pills */
.summary-row { display: flex; flex-wrap: wrap; gap: 8px; }
.summary-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 12px; border-radius: 20px; border: 1.5px solid transparent;
  font-size: 12px; font-weight: 500; cursor: pointer;
  background: #f5f5f5; color: #555; transition: all .15s;
}
.summary-pill:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,.1); }
.pill-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.pill-count { background: rgba(0,0,0,.08); border-radius: 10px; padding: 1px 7px; font-size: 11px; }

.pill--validated { color: #2e7d32; background: #e8f5e9; border-color: #a5d6a7; }
.pill--partial   { color: #e65100; background: #fff3e0; border-color: #ffcc80; }
.pill--failed    { color: #c62828; background: #ffebee; border-color: #ef9a9a; }
.pill--no_match  { color: #bf360c; background: #fbe9e7; border-color: #ffab91; }
.pill--cancelled { color: #546e7a; background: #eceff1; border-color: #b0bec5; }
.pill--matched   { color: #01579b; background: #e1f5fe; border-color: #81d4fa; }
.pill--active    { box-shadow: 0 0 0 2px currentColor; }

/* Filters */
.filter-bar { border-top: 1px solid #f0f0f0; padding-top: 16px; }
.filter-input >>> .v-input__slot { border-radius: 8px !important; }

/* Table */
.sms-table >>> .v-data-table__wrapper { border-radius: 0 0 12px 12px; }
.sms-table >>> thead th {
  font-size: 11px !important; font-weight: 600 !important;
  text-transform: uppercase; letter-spacing: .06em;
  color: #9e9e9e !important; background: #fafafa !important;
  border-bottom: 1px solid #eeeeee !important;
}
.sms-table >>> tbody tr { transition: background .1s; }
.sms-table >>> tbody tr:hover { background: #f9fbff !important; }
.sms-table >>> tbody td { padding: 10px 16px !important; border-bottom: 1px solid #f5f5f5 !important; }

/* Status badge */
.status-badge {
  display: inline-flex; align-items: center;
  padding: 3px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 600; letter-spacing: .03em;
}
.status-badge--lg { font-size: 12px; padding: 4px 12px; }
.status-badge--sm { font-size: 10px; padding: 2px 8px; }
.badge--validated { background: #e8f5e9; color: #2e7d32; }
.badge--partial   { background: #fff3e0; color: #e65100; }
.badge--failed    { background: #ffebee; color: #c62828; }
.badge--no_match  { background: #fbe9e7; color: #bf360c; }
.badge--cancelled { background: #eceff1; color: #546e7a; }
.badge--matched   { background: #e1f5fe; color: #01579b; }

/* Table cells */
.cell-date    { font-size: 12px; font-weight: 500; color: #333; }
.cell-attempts{ font-size: 11px; color: #9e9e9e; margin-top: 2px; }
.cell-feature { font-size: 13px; font-weight: 500; color: #212121; }
.cell-user    { font-size: 13px; color: #333; }
.cell-phone   { font-size: 13px; font-weight: 500; color: #333; }
.cell-sub     { font-size: 11px; color: #9e9e9e; margin-top: 1px; }
.cell-id      { font-size: 12px; color: #555; font-family: monospace; margin-top: 1px; }

.amount-block { font-size: 12px; }
.amount-label { color: #9e9e9e; margin-right: 4px; }
.amount-value { font-weight: 600; color: #212121; }
.amount-partial{ color: #e65100; font-size: 11px; margin-top: 2px; }

/* Action buttons */
.action-row { display: flex; gap: 4px; flex-wrap: wrap; }
.action-btn {
  border-radius: 6px !important; text-transform: none !important;
  font-size: 11px !important; font-weight: 500 !important; letter-spacing: 0 !important;
}
.action-btn--view   { background: #e3f2fd !important; color: #1565c0 !important; }
.action-btn--retry  { background: #fff8e1 !important; color: #e65100 !important; }
.action-btn--cancel { background: #ffebee !important; color: #c62828 !important; }

.empty-state { padding: 40px; text-align: center; }

/* Detail dialog */
.detail-card { border-radius: 12px !important; overflow: hidden; }
.detail-header {
  padding: 20px 24px; display: flex; align-items: flex-start;
  justify-content: space-between; position: relative;
}
.detail-header--validated { background: linear-gradient(135deg, #2e7d32, #43a047); }
.detail-header--partial   { background: linear-gradient(135deg, #bf360c, #e64a19); }
.detail-header--failed    { background: linear-gradient(135deg, #b71c1c, #c62828); }
.detail-header--no_match  { background: linear-gradient(135deg, #bf360c, #d84315); }
.detail-header--cancelled { background: linear-gradient(135deg, #37474f, #546e7a); }
.detail-header--matched   { background: linear-gradient(135deg, #01579b, #0277bd); }

.detail-title { font-size: 16px; font-weight: 600; color: #fff; margin-bottom: 8px; }
.detail-close { position: absolute; top: 12px; right: 12px; }
.detail-body  { padding: 20px 24px !important; }

/* Info grid */
.info-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;
  background: #f8f9fa; border-radius: 10px; padding: 16px;
}
.info-label { font-size: 11px; color: #9e9e9e; font-weight: 600;
              text-transform: uppercase; letter-spacing: .05em; margin-bottom: 3px; }
.info-value { font-size: 13px; color: #212121; font-weight: 500; }
.info-value.highlight { font-size: 15px; color: #1565c0; font-weight: 700; }
.id-chip {
  display: inline-block; background: #e8eaf6; color: #3949ab;
  border-radius: 4px; padding: 1px 6px; font-size: 11px;
  font-family: monospace; margin-left: 4px;
}

.error-banner {
  background: #ffebee; border-left: 3px solid #c62828;
  padding: 10px 14px; border-radius: 0 6px 6px 0;
  font-size: 13px; color: #b71c1c;
}

/* SMS body */
.sms-body { }
.section-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .07em; color: #9e9e9e; margin-bottom: 6px;
}
.sms-text {
  background: #1a1a2e; color: #e0e0e0;
  font-family: 'Courier New', monospace; font-size: 12px;
  padding: 14px 16px; border-radius: 8px; line-height: 1.7;
  white-space: pre-wrap; word-break: break-word;
}

/* Timeline */
.attempt-count {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 50%;
  background: #e0e0e0; color: #555; font-size: 11px;
  font-weight: 700; margin-left: 6px;
}
.timeline { position: relative; }
.timeline-item {
  display: flex; gap: 12px; padding-bottom: 16px; position: relative;
}
.timeline-item--latest .timeline-content {
  background: #f8f9ff; border-radius: 8px; padding: 10px 12px;
  border: 1px solid #e8eaf6;
}
.timeline-content { flex: 1; padding: 4px 0; }
.timeline-dot {
  width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0;
  margin-top: 4px; border: 2px solid #fff; box-shadow: 0 0 0 2px currentColor;
}
.dot--validated { color: #2e7d32; background: #2e7d32; }
.dot--partial   { color: #e65100; background: #e65100; }
.dot--failed    { color: #c62828; background: #c62828; }
.dot--no_match  { color: #bf360c; background: #bf360c; }
.dot--cancelled { color: #546e7a; background: #546e7a; }
.dot--matched   { color: #01579b; background: #01579b; }

.timeline-meta  { font-size: 11px; color: #9e9e9e; margin-top: 3px; }
.timeline-error { font-size: 11px; color: #c62828; margin-top: 4px; }
.text-muted     { opacity: .6; }
.latest-chip {
  font-size: 10px; background: #e8eaf6; color: #3949ab;
  padding: 1px 8px; border-radius: 10px; font-weight: 600;
}
</style>
